import {Chip} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {addToast} from "@heroui/toast";
import {Icon} from "@iconify/react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute} from '@tanstack/react-router'
import React from "react";
import {apiActionImproved, apiRetrieveImproved} from "../../../api";
import PButton from "../../../components/PButton.tsx";
import Header from "../../../Header.tsx";

export const Route = createFileRoute('/_app/master/tournament-placements')({
    component: RouteComponent,
})

type Placement = {
    uid: string; title: string; sport_type?: string; dates?: string; location?: string;
    url?: string; description?: string; contact_name?: string; contact_email?: string;
    contact_phone?: string; image_url?: string; status: string; created_at?: string;
};

function statusChip(s: string) {
    if (s === 'approved') return <Chip color="success" size="sm" variant="flat">Approved</Chip>;
    if (s === 'rejected') return <Chip color="danger" size="sm" variant="flat">Rejected</Chip>;
    return <Chip color="warning" size="sm" variant="flat">Pending</Chip>;
}

function RouteComponent() {
    const queryClient = useQueryClient();

    const query = useQuery<{placements: Placement[]}>({
        queryKey: ['master-tournament-placements'],
        queryFn:  () => apiRetrieveImproved('master/tournament-placements' as any),
    });

    const setStatus = useMutation({
        mutationFn: async ({uid, status}: {uid: string; status: string}) =>
            apiActionImproved(`master/tournament-placements/${uid}/status` as any, {status}, {method: 'POST'}),
        // Optimistically flip the row's status so the change is instant and obvious.
        onMutate:   async ({uid, status}) => {
            await queryClient.cancelQueries({queryKey: ['master-tournament-placements']});
            const prev = queryClient.getQueryData<{placements: Placement[]}>(['master-tournament-placements']);
            queryClient.setQueryData<{placements: Placement[]}>(['master-tournament-placements'], (old) =>
                old ? {...old, placements: old.placements.map((p) => p.uid === uid ? {...p, status} : p)} : old);
            return {prev};
        },
        onError:    (_e, _v, ctx) => {
            if (ctx?.prev) queryClient.setQueryData(['master-tournament-placements'], ctx.prev);
            addToast({title: 'Could not update status', color: 'danger'});
        },
        onSuccess:  (_d, {status}) => addToast({
            title: status === 'approved' ? 'Placement approved' : status === 'rejected' ? 'Placement rejected' : 'Placement updated',
            color: status === 'rejected' ? 'warning' : 'success',
        }),
        onSettled:  () => queryClient.invalidateQueries({queryKey: ['master-tournament-placements']}),
    });

    const placements = query.data?.placements || [];

    return (
        <>
            <Header />
            <div className="mx-auto w-11/12 max-w-5xl py-4">
                <h1 className="mb-1 text-2xl font-bold">Tournament Placements</h1>
                <p className="mb-4 text-sm text-default-500">External tournaments submitted for the directory. Approve to publish.</p>

                {query.isLoading ? (
                    <div className="flex justify-center py-12"><Spinner size="lg" /></div>
                ) : placements.length === 0 ? (
                    <div className="py-12 text-center text-default-500">No submissions yet.</div>
                ) : (
                    <div className="grid gap-4">
                        {placements.map((p) => (
                            <div key={p.uid} className="flex gap-4 rounded-xl border border-default-200 bg-content1 p-4">
                                {p.image_url && <img src={p.image_url} alt="" className="h-20 w-20 shrink-0 rounded-lg object-cover" />}
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <h3 className="truncate font-semibold">{p.title}</h3>
                                        {statusChip(p.status)}
                                    </div>
                                    <div className="mt-1 grid gap-0.5 text-sm">
                                        <div className="capitalize text-default-500">{[p.sport_type, p.dates, p.location].filter(Boolean).join(' · ') || '—'}</div>
                                        {p.url && <a href={p.url} target="_blank" rel="noopener noreferrer" className="truncate text-primary">{p.url}</a>}
                                        {p.description && <div className="whitespace-pre-line text-default-600">{p.description}</div>}
                                        <div className="mt-1 text-default-500">
                                            Submitted by {p.contact_name} — <a href={`mailto:${p.contact_email}`} className="text-primary">{p.contact_email}</a>
                                            {p.contact_phone ? ` · ${p.contact_phone}` : ''}
                                        </div>
                                    </div>
                                    <div className="mt-3 flex gap-2">
                                        <PButton size="sm" color="success" variant={p.status === 'approved' ? 'solid' : 'flat'}
                                                 isDisabled={p.status === 'approved' || setStatus.isPending}
                                                 startContent={<Icon icon="material-symbols-light:check" className="text-lg" />}
                                                 onPress={() => setStatus.mutate({uid: p.uid, status: 'approved'})}>Approve</PButton>
                                        <PButton size="sm" color="danger" variant={p.status === 'rejected' ? 'solid' : 'flat'}
                                                 isDisabled={p.status === 'rejected' || setStatus.isPending}
                                                 startContent={<Icon icon="material-symbols-light:close" className="text-lg" />}
                                                 onPress={() => setStatus.mutate({uid: p.uid, status: 'rejected'})}>Reject</PButton>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}
